import { useEffect } from "react";
import apiClient from "@/lib/api-client";

/** Cada cuánto se repite el latido. El servidor da cada uno por bueno 45 s. */
const HEARTBEAT_MS = 20_000;

/** El chat está «delante»: la pestaña se ve y la ventana tiene el foco. */
function isOnScreen(): boolean {
  return document.visibilityState === "visible" && document.hasFocus();
}

/**
 * Mientras el chat `conversationId` esté abierto Y a la vista, le dice al
 * servidor «lo estoy mirando» para que no te avise de cada mensaje de ese
 * cliente, ni en el navegador ni en el móvil (15-oct-2026, petición de Luis;
 * ver backend `push/conversation-presence.ts`). Si cambias de pestaña o de
 * programa, o cierras el chat, los avisos vuelven al momento.
 *
 * `null` = no hay chat abierto (o es el inbox de soporte de plataforma, que no
 * manda avisos). Best-effort: si una llamada falla, como mucho llega un aviso.
 */
export function useConversationPresence(conversationId: string | null): void {
  useEffect(() => {
    if (!conversationId) return;
    const path = `/whatsapp-conversations/${conversationId}/viewing`;
    let viewing = false;

    const send = (next: boolean) => {
      viewing = next;
      apiClient.post(path, { viewing: next }).catch(() => {
        /* sin latido, como mucho llega un aviso */
      });
    };
    const sync = () => {
      if (isOnScreen()) send(true);
      else if (viewing) send(false);
    };

    sync();
    const timer = window.setInterval(() => {
      if (isOnScreen()) send(true);
    }, HEARTBEAT_MS);
    window.addEventListener("focus", sync);
    window.addEventListener("blur", sync);
    document.addEventListener("visibilitychange", sync);

    return () => {
      window.clearInterval(timer);
      window.removeEventListener("focus", sync);
      window.removeEventListener("blur", sync);
      document.removeEventListener("visibilitychange", sync);
      if (viewing) send(false);
    };
  }, [conversationId]);
}
